import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import WorkflowsTable from '../components/workflows/WorkflowsTable';
import useAdminWorkflows from '../hooks/useAdminWorkflows.js';
import useTags from '../hooks/useTags';
import FilterFieldSelect from '../components/forms/FilterFieldSelect';
import FilterFieldCheck from '../components/forms/FilterFieldCheck';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'deprecated', label: 'Deprecated' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'standard', label: 'Standard' },
  { value: 'self_startable', label: 'Self-startable' },
  { value: 'user', label: 'User workflow' },
];

export default function WorkflowsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const { data: workflows } = useAdminWorkflows(searchParamsKey);
  const { tags } = useTags();
  const navigate = useNavigate();
  useDocumentTitle('Workflows – Maintella');

  const status = searchParams.get('status') || 'all';
  const type = searchParams.get('type') || 'all';
  const hasPendingDraft = searchParams.get('has_pending_draft') === '1';
  const tagStatusUnscoped = searchParams.get('tag_status') === 'unscoped';

  const setStatusFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('status');
    else next.set('status', value);
    setSearchParams(next, { replace: true });
  };

  const setTypeFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('type');
    else next.set('type', value);
    setSearchParams(next, { replace: true });
  };

  const setPendingDraftFilter = (checked) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set('has_pending_draft', '1');
    else next.delete('has_pending_draft');
    setSearchParams(next, { replace: true });
  };

  const setTagStatusFilter = (checked) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set('tag_status', 'unscoped');
    else next.delete('tag_status');
    setSearchParams(next, { replace: true });
  };

  const workflowFilterExtras = useMemo(
    () => (
      <>
        <FilterFieldSelect
          id="workflows-filter-status"
          label="Status"
          value={status}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={STATUS_OPTIONS}
        />
        <FilterFieldSelect
          id="workflows-filter-type"
          label="Type"
          value={type}
          onChange={(e) => setTypeFilter(e.target.value)}
          options={TYPE_OPTIONS}
        />
        <FilterFieldCheck
          id="workflows-filter-pending-draft"
          label="Pending draft"
          checked={hasPendingDraft}
          onChange={(e) => setPendingDraftFilter(e.target.checked)}
        />
        <FilterFieldCheck
          id="workflows-filter-tag-status"
          label="Needs scoping"
          checked={tagStatusUnscoped}
          onChange={(e) => setTagStatusFilter(e.target.checked)}
        />
      </>
    ),
    // searchParamsKey keeps URL-driven handlers in sync; value props track filter state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, type, hasPendingDraft, tagStatusUnscoped, searchParamsKey]
  );

  return (
    <>
      <ObjectTopBar
        title="Workflows"
        primaryBtn={{
          btnText: 'New Workflow',
          btnIcon: 'bi bi-plus-lg',
          variant: 'primary',
          onClick: () => navigate('/admin/flow/new'), 
        }}
      />
      <div className="container py-4">
        <WorkflowsTable items={workflows} filterExtras={workflowFilterExtras} tags={tags} />
      </div>
    </>
  );
}
